import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMenu } from '../../../shared/hooks/useMenu.js';
import { ForbiddenPage } from '../screens/error/ForbiddenPage.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * RequirePageAccess — path-based, menu-driven route guard (X-RBAC-1).
 *
 * Composes inside {@link RequireAuth} to add per-page RBAC on top of the
 * plain authentication check:
 *
 *   <RequireAuth>
 *     <RequirePageAccess path="/admin/users">
 *       <UserListPage />
 *     </RequirePageAccess>
 *   </RequireAuth>
 *
 * Access is decided ENTIRELY by the administrator through the Role Manager
 * permission matrix, surfaced to the client via `GET /api/me/menu`
 * ({@link useMenu}). Nothing is hardcoded as admin-only in code — a page is
 * accessible iff the caller's roles have been granted a menu item for it. This
 * honours the permanent "ADMIN DECIDES PAGE ACCESS" rule.
 *
 * Behaviour:
 * - While the menu is loading, a status placeholder is shown.
 * - If the menu request FAILS, the guard fails OPEN (renders children). The
 *   backend IamAuthzGuard / @RequirePermission remain the real enforcement
 *   boundary on every data call, so a transient menu-endpoint outage degrades
 *   to auth-only UX rather than locking every user out of the whole app.
 * - Once the menu is loaded, the page renders iff a visible menu item matches
 *   `path` (defaults to the current location); otherwise the 403
 *   {@link ForbiddenPage} is shown.
 */
export function RequirePageAccess({
  path,
  children,
}: {
  /** Canonical menu path to check. Defaults to the current location. */
  readonly path?: string;
  readonly children: ReactNode;
}): ReactNode {
  const location = useLocation();
  const { token } = useAuth();
  const { menu, loading, error } = useMenu('/api', token, 'web');

  const target = path ?? location.pathname;

  if (loading) return <p role="status">Loading…</p>;
  // Fail open on menu-fetch failure — server-side guards still enforce RBAC.
  if (error) return <>{children}</>;

  const items = menu?.items ?? [];
  const granted = items.some(
    (it) => it.isVisible && (it.path === target || target.startsWith(`${it.path}/`)),
  );

  if (!granted) return <ForbiddenPage />;
  return <>{children}</>;
}
